import { RecordContextProvider, useListContext, useTranslate } from 'ra-core';
import { Pencil } from 'lucide-react';
import { DataTable } from '../../components/admin/data-table';
import { DateField } from '../../components/admin/date-field';
import { ActionLink } from '../../components/custom/ActionLink';
import { RowActions } from '../../components/custom/RowActions';
import { useIsUnitManager } from '../../auth/useIsUnitManager';
import { useCurrentUser } from '../../auth/currentUserContext';
import { MoonstarDeleteButton } from '../Moonstar/MoonstarDeleteButton';
import { MoonstarList } from '../Moonstar/MoonstarList';
import { MoonstarListActions } from '../Moonstar/MoonstarListActions';
import { ChecklistPercentageField } from './ChecklistMetricField';
import {
  ChecklistDateRangeFilter,
  DEFAULT_CHECKLIST_DATE_RANGE_OPTION,
  getPresetRangeFilter,
} from './ChecklistsListFilters';
import { getChecklistLandingPath } from './checklistNavigation';

interface ChecklistListEntry {
  id: number | string;
  title: string;
  startOn: string;
  endOn?: string;
  assigneeUserGroups: string[];
  createdByUserFullName: string;
  createdOn: string;
  completionRatio?: number;
  onTimeRatio?: number;
  overdueTasksCount: number;
}

export function ChecklistsListPage() {
  const isUnitManager = useIsUnitManager();
  const { units } = useCurrentUser();
  const filters = [
    <ChecklistDateRangeFilter
      key="date-range"
      label="moonstar.checklists.filters.dateRange"
      alwaysOn
    />,
  ];

  return (
    <MoonstarList<ChecklistListEntry>
      filters={filters}
      sort={{ field: 'createdOn', order: 'DESC' }}
      filterDefaultValues={getPresetRangeFilter(
        DEFAULT_CHECKLIST_DATE_RANGE_OPTION
      )}
      actions={<MoonstarListActions hideCreate={isUnitManager} />}
    >
      <ChecklistTable units={units} isUnitManager={isUnitManager} />
    </MoonstarList>
  );
}

function ChecklistTable({
  units,
  isUnitManager,
}: {
  units: string[];
  isUnitManager: boolean;
}) {
  const { filterValues } = useListContext();

  return (
    <DataTable
      bulkActionButtons={false}
      rowClick={(id) => getChecklistLandingPath(id, units.length, filterValues)}
    >
      <DataTable.Col<ChecklistListEntry> source="title" disableSort />
      <DataTable.Col<ChecklistListEntry>
        source="assigneeUserGroups"
        disableSort
        render={(record) => formatAssigneeGroups(record.assigneeUserGroups)}
      />
      <DataTable.Col<ChecklistListEntry>
        source="completionRatio"
        disableSort
        className="w-px whitespace-nowrap"
        render={(record) => (
          <ChecklistPercentageField value={record.completionRatio} />
        )}
      />
      <DataTable.Col<ChecklistListEntry>
        source="onTimeRatio"
        disableSort
        className="w-px whitespace-nowrap"
        render={(record) => (
          <ChecklistPercentageField value={record.onTimeRatio} />
        )}
      />
      <DataTable.Col<ChecklistListEntry>
        source="overdueTasksCount"
        disableSort
        className="w-px whitespace-nowrap"
      />
      <DataTable.Col<ChecklistListEntry>
        source="startOn"
        disableSort
        className="w-px whitespace-nowrap"
      >
        <DateField source="startOn" showTime empty="—" />
      </DataTable.Col>
      <DataTable.Col<ChecklistListEntry>
        source="endOn"
        disableSort
        className="w-px whitespace-nowrap"
      >
        <DateField source="endOn" showTime empty="—" />
      </DataTable.Col>
      <DataTable.Col<ChecklistListEntry>
        source="createdByUserFullName"
        disableSort
        render={(record) => record.createdByUserFullName || '—'}
      />
      <DataTable.Col<ChecklistListEntry>
        source="createdOn"
        className="w-px whitespace-nowrap"
      >
        <DateField source="createdOn" empty="—" />
      </DataTable.Col>
      {!isUnitManager && (
        <DataTable.Col<ChecklistListEntry>
          className="w-px whitespace-nowrap"
          render={(record) => <ChecklistRowActions id={record.id} />}
        />
      )}
    </DataTable>
  );
}

function ChecklistRowActions({ id }: { id: ChecklistListEntry['id'] }) {
  const translate = useTranslate();

  return (
    <RowActions orientation="column">
      <ActionLink to={`/checklists/${id}/edit`} icon={<Pencil />}>
        {translate('ra.action.edit')}
      </ActionLink>
      <RecordContextProvider value={{ id }}>
        <MoonstarDeleteButton resource="checklists" />
      </RecordContextProvider>
    </RowActions>
  );
}

function formatAssigneeGroups(names: string[]) {
  return names.length > 0 ? names.join(', ') : '—';
}
